import React from 'react';
import { formatujPercentoBezZnamienkaText } from './utils/formatovanie.js';
import { ziskajLogoBanky, ziskajStylLogaBanky } from './utils/bankoveLoga.js';
import HintPopover from './components/HintPopover.js';
import alertTriangleIkona from './obr/alert-triangle.svg';
import circlePlusMinusIkona from './obr/circle-plus-minus.svg';
import infoCircleIkona from './obr/info-circle.svg';
import rosetteDiscountIkona from './obr/rosette-discount.svg';

const jeCislo = (v) => Number.isFinite(Number(v));

const zostavTooltipZlavyPrirazky = (banka) => {
  const arr = Array.isArray(banka?.zlavyPrirazky) ? banka.zlavyPrirazky : [];
  if (!arr.length) return '';

  const riadky = ['Zľavy a prirážky:'];

  arr.forEach((z) => {
    const nazov = z?.nazov ?? '';
    const hodnotaText = z?.hodnotaText ?? (jeCislo(z?.hodnota) ? String(z.hodnota) : '');
    const hlavnyRiadok = `${nazov} ${hodnotaText}`.trim();

    if (hlavnyRiadok) {
      riadky.push(hlavnyRiadok);
    }

    if (z?.podpoznamka) {
      riadky.push(`Pozn.: ${z.podpoznamka}`);
    }
  });

  return riadky.join('\n').trim();
};

const zostavTooltipUpozornenia = (upozorneniaText) => {
  if (!Array.isArray(upozorneniaText) || !upozorneniaText.length) return '';
  return ['Upozornenia:', ...upozorneniaText].join('\n').trim();
};

const zostavTooltipKampan = (banka) => {
  // BE preferovaný výstup: banka.kampane (pole uplatnených kampaní)
  const arrRaw =
    (Array.isArray(banka?.kampane) && banka.kampane.length ? banka.kampane : null) ||
    (Array.isArray(banka?.info?.kampane) && banka.info.kampane.length ? banka.info.kampane : null) ||
    null;

  // fallback na starý formát: banka.kampan (1 kampaň)
  if (!arrRaw) {
    const k = banka?.kampan;
    if (!k || k?.aktivna !== true) return '';
    const nazov = k?.nazov ? `Kampaň: ${k.nazov}` : 'Kampaň';
    const hint = k?.hintText ? `\n${k.hintText}` : '';
    return `${nazov}${hint}`.trim();
  }

  const arr = arrRaw
    .filter((k) => k?.aktivna === true)
    .map((k) => {
      const nazov = k?.nazov ? String(k.nazov) : '';
      const hint = k?.hintText ? String(k.hintText) : '';
      if (!nazov && !hint) return '';
      return hint ? `${nazov}\n${hint}`.trim() : nazov.trim();
    })
    .filter(Boolean);

  if (!arr.length) return '';

  // viac kampaní → tooltip v jednom title, oddelené " | "
  if (arr.length === 1) return `Kampaň:\n${arr[0]}`.trim();
  return `Kampane:\n${arr.join('\n\n')}`.trim();
};

const zostavTooltipInfo = (banka, eur) => {
  const poznamka = banka?.infoIkonka?.poznamka ? String(banka.infoIkonka.poznamka).trim() : '';
  const zm = banka?.infoIkonka?.zivotneMinima || null;

  const riadky = [];

  if (poznamka) {
    riadky.push(poznamka);
  }

  if (zm && (zm.jednotlivec != null || zm.druhyDospely != null || zm.dieta != null)) {
    riadky.push(''); // prázdny riadok (oddelenie)
    riadky.push('Životné minimá:');

    if (zm.jednotlivec != null) riadky.push(`Jeden dospelý – ${eur(zm.jednotlivec)} €`);
    if (zm.druhyDospely != null) riadky.push(`Ďalší dospelý – ${eur(zm.druhyDospely)} €`);
    if (zm.dieta != null) riadky.push(`Dieťa – ${eur(zm.dieta)} €`);
  }

  const tip = riadky.join('\n').trim();
  return tip ? tip : '';
};

function PorovnavacBankaKarta({ banka, suma, ltvPercenta, eur, upozorneniaText, fixaciaRoky, splatnostRoky }) {
  const bankaNazov = banka?.bankaKod ?? banka?.bankaNazov ?? 'Banka';
  const logoBanky = ziskajLogoBanky(bankaNazov);
  const stylLogaBanky = ziskajStylLogaBanky(logoBanky);

  const finalRate = jeCislo(banka?.finalRate) ? Number(banka.finalRate) : null;
  const splatka = jeCislo(banka?.splatka) ? Number(banka.splatka) : null;
  const stressSplatka = jeCislo(banka?.stressSplatka) ? Number(banka.stressSplatka) : null;
  const spracPoplatok = jeCislo(banka?.spracPoplatok) ? Number(banka.spracPoplatok) : null;
  const spracPoplatokText = banka?.spracPoplatokText ? String(banka.spracPoplatokText) : null;

  const maxVyskaUveru = jeCislo(banka?.maxVyskaUveru) ? Number(banka.maxVyskaUveru) : null;
  const dstiMaxSplatka = jeCislo(banka?.dstiMaxSplatka) ? Number(banka.dstiMaxSplatka) : null;

  const jeAtipickaFixacia = banka?.jeAtipickaFixacia === true;
  const fixaciaMesiace = jeAtipickaFixacia ? (Number(banka?.fixaciaMesiace) || null) : null;

  const fixText = jeAtipickaFixacia && fixaciaMesiace
  ? ` (${fixaciaMesiace}Mes Fix)`
  : (Number.isFinite(Number(fixaciaRoky)) && Number(fixaciaRoky) > 0 ? ` (${Number(fixaciaRoky)}R Fix)` : '');

  const splatnost = Number.isFinite(Number(splatnostRoky)) && Number(splatnostRoky) > 0 ? Number(splatnostRoky) : null;
  const tipZlavy = zostavTooltipZlavyPrirazky(banka);
  const tipUpoz = zostavTooltipUpozornenia(upozorneniaText);
  const tipKampan = zostavTooltipKampan(banka);
  const tipInfo = zostavTooltipInfo(banka, eur);
  const maKampan = !!tipKampan;
  const maUpoz = !!tipUpoz;
  const maZlavy = !!tipZlavy;
  const maInfo = !!tipInfo;

  const poruseneLimity = banka?.poruseneLimity || {};

  const stylPorusenyLimit = {
    color: 'var(--danger, #c62828)'
  };

  const nedostupnostText = [];

    if (banka?.nedostupnost?.nemaTypHypoteky === true || banka?.nedostupnost?.nemaTypHypotekyPreBanku === true) {
      nedostupnostText.push('Banka neponúka daný typ hypotéky.');
    }

    if (banka?.nedostupnost?.nemaFixaciu === true || banka?.nedostupnost?.nemaFixaciuPreBanku === true) {
      nedostupnostText.push('Banka neponúka daný typ fixácie.');
    }

  const jeNedostupna =
    banka?.nedostupnost?.nemaFixaciu === true ||
    banka?.nedostupnost?.nemaTypHypoteky === true ||
    banka?.nedostupnost?.nemaTypHypotekyPreBanku === true ||
    banka?.nedostupnost?.nemaFixaciuPreBanku === true;

  return (
    <div className="porovnavac-banka-card">
      <div className="porovnavac-banka-head">
        <div className="porovnavac-banka-nazov banka-head-with-logo">
          {logoBanky ? (
            <span
              className={`banka-logo-obal banka-logo-obal--${logoBanky.variant || "wide"}`}
              style={stylLogaBanky}
            >
              {logoBanky.srcDark ? (
                <>
                  <img
                    src={logoBanky.src}
                    alt={`${bankaNazov} logo`}
                    className={`banka-logo banka-logo--light banka-logo--fit-${logoBanky.fit || "contain"}`}
                  />
                  <img
                    src={logoBanky.srcDark}
                    alt={`${bankaNazov} logo`}
                    className={`banka-logo banka-logo--dark banka-logo--fit-${logoBanky.fit || "contain"}`}
                  />
                </>
              ) : (
                <img
                  src={logoBanky.src}
                  alt={`${bankaNazov} logo`}
                  className={`banka-logo banka-logo--fit-${logoBanky.fit || "contain"}`}
                />
              )}
            </span>
          ) : (
            <span className="banka-head-with-logo__kod banka-head-with-logo__kod--fallback">
              {bankaNazov}
            </span>
          )}
        </div>

        <div className="porovnavac-banka-urok">
          {finalRate !== null ? formatujPercentoBezZnamienkaText(finalRate) : ' '}
          {fixText}
        </div>
      </div>

      {jeNedostupna ? (
        <div className="porovnavac-banka-nedostupna">
          {nedostupnostText.map((t, idx) => (
            <div key={idx}>{t}</div>
          ))}
        </div>
      ) : (
         <>
      <div className="porovnavac-banka-grid">

        <div className="porovnavac-banka-m">
            <div className="k">Sprac. poplatok</div>
            <div className="v">
                {spracPoplatokText ? spracPoplatokText : (spracPoplatok !== null ? `${eur(spracPoplatok)} €` : '—')}
            </div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Výška úveru</div>
          <div className="v" style={poruseneLimity.vyskaUveru ? stylPorusenyLimit : undefined}>
            {`${eur(suma)} €`}</div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">LTV</div>
           <div className="v" style={poruseneLimity.ltv ? stylPorusenyLimit : undefined}>
            {ltvPercenta !== null ? `${(Math.round(ltvPercenta * 10) / 10).toString().replace('.', ',')} %` : '—'}
          </div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Splatnosť</div>
          <div className="v" style={poruseneLimity.splatnost ? stylPorusenyLimit : undefined}>
            {splatnost !== null ? `${splatnost} rokov` : '—'}</div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Splátka</div>
          <div className="v">{splatka !== null ? `${eur(splatka)} €` : '—'}</div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Stress splátka</div>
          <div className="v" style={poruseneLimity.stressSplatka ? stylPorusenyLimit : undefined}>
            {stressSplatka !== null ? `${eur(stressSplatka)} €` : '—'}</div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Max. výška úveru</div>
          <div className="v">{maxVyskaUveru !== null ? `${eur(maxVyskaUveru)} €` : '—'}</div>
        </div>

        <div className="porovnavac-banka-m">
          <div className="k">Max. splátka (DSTI)</div>
          <div className="v">{dstiMaxSplatka !== null ? `${eur(dstiMaxSplatka)} €` : '—'}</div>
        </div>
      </div>

      <div className="porovnavac-banka-ikonky">
        {maKampan && (
          <HintPopover
            obsah={tipKampan}
            ariaLabel="Zobraziť detail kampane"
            pozicia="top"
            triggerClassName="porovnavac-ikona"
          >
            <img src={rosetteDiscountIkona} alt="Kampaň" className="porovnavac-ikona-img" />
          </HintPopover>
        )}

        {maZlavy && (
          <HintPopover
            obsah={tipZlavy}
            ariaLabel="Zobraziť detail zliav a prirážok"
            pozicia="top"
            triggerClassName="porovnavac-ikona"
          >
            <img src={circlePlusMinusIkona} alt="Zľavy a prirážky" className="porovnavac-ikona-img" />
          </HintPopover>
        )}

        {maUpoz && (
          <HintPopover
            obsah={tipUpoz}
            ariaLabel="Zobraziť upozornenia"
            pozicia="top"
            triggerClassName="porovnavac-ikona"
          >
            <img src={alertTriangleIkona} alt="Upozornenie" className="porovnavac-ikona-img" />
          </HintPopover>
        )}

        {maInfo && (
          <HintPopover
            obsah={tipInfo}
            ariaLabel="Zobraziť doplňujúce informácie"
            pozicia="top"
            triggerClassName="porovnavac-ikona"
          >
            <img src={infoCircleIkona} alt="Info" className="porovnavac-ikona-img" />
          </HintPopover>
        )}

        <span className="porovnavac-banka-kod-spodok">
          {bankaNazov}
        </span>
      </div>
         </>
       )}   
    </div>
     
  );
}

export default PorovnavacBankaKarta;